import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import adminService from '../../services/adminService';
import PageHeader from '../../components/common/PageHeader';
import { API_BASE_URL } from '../../constants/config';

const BannerManagement = () => {
    const [currentBanner, setCurrentBanner] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        fetchBannerSettings();
    }, []);

    const normalizeBannerUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('data:') || url.startsWith('http')) return url;

        const baseUrl = API_BASE_URL.split('/main/api/v1')[0];
        return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
    };

    const fetchBannerSettings = async () => {
        try {
            setFetching(true);
            const response = await adminService.getBannerSettings();
            // Handle various response structures: { data: { bannerImageUrl } } or { data: { data: { bannerImageUrl } } } or { bannerImageUrl }
            const bannerUrlData = response?.data?.data?.bannerImageUrl || response?.data?.bannerImageUrl || response?.bannerImageUrl;

            if (bannerUrlData) {
                const normalizedUrl = normalizeBannerUrl(bannerUrlData);
                setCurrentBanner(normalizedUrl);
            }
        } catch (error) {
            console.error('Error fetching banner settings:', error);
        } finally {
            setFetching(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setMessage({ type: 'error', text: 'File size too large. Max 5MB allowed.' });
                return;
            }
            setSelectedFile(file);
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            setMessage({ type: '', text: '' });
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!selectedFile) return;

        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('file', selectedFile);

            const response = await adminService.updateBannerImage(formData);
            if (response.status === 'success' || response.success) {
                setMessage({ type: 'success', text: 'Banner updated successfully!' });

                const bannerUrlData = response.data?.bannerImageUrl || response.bannerImageUrl;
                if (bannerUrlData) {
                    const normalizedUrl = normalizeBannerUrl(bannerUrlData);
                    // Add cache buster to refresh the image immediately
                    setCurrentBanner(`${normalizedUrl}${normalizedUrl.includes('?') ? '&' : '?'}t=${Date.now()}`);
                }

                setSelectedFile(null);
                setPreviewUrl(null);
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update banner.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-primary-50 pb-12">
            <PageHeader
                title="Hero Banner Management"
                subtitle="Update the main image displayed on the home page hero section"
                icon={
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                }
            />

            <div className="max-w-4xl mx-auto px-4 mt-8">
                <div className="bg-white rounded-2xl shadow-sm border border-primary-100 overflow-hidden">
                    <div className="p-6 md:p-8">
                        <div className="mb-8">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Current Banner</h3>
                            <div className="relative aspect-[21/9] bg-gray-100 rounded-xl overflow-hidden border border-gray-200 shadow-inner">
                                {fetching ? (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                                    </div>
                                ) : currentBanner ? (
                                    <img src={currentBanner} alt="Current Banner" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                                        <svg className="w-12 h-12 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <p>No custom banner set. Using default image.</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <form onSubmit={handleUpload}>
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Upload New Banner</h3>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Select Image File</label>
                                <div className="flex items-center justify-center w-full">
                                    <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-300 border-dashed rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition duration-150">
                                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                            <svg className="w-10 h-10 mb-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                            </svg>
                                            <p className="mb-2 text-sm text-gray-500"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                                            <p className="text-xs text-gray-400">PNG, JPG or WEBP (MAX. 5MB)</p>
                                        </div>
                                        <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                                    </label>
                                </div>
                            </div>

                            {previewUrl && (
                                <div className="mb-8">
                                    <h4 className="text-sm font-semibold text-gray-600 mb-3">Preview</h4>
                                    <div className="relative aspect-[21/9] bg-gray-100 rounded-xl overflow-hidden border-2 border-primary-300 shadow-md">
                                        <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                                    </div>
                                </div>
                            )}

                            {message.text && (
                                <div className={`p-4 rounded-xl mb-6 flex items-center ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                    {message.type === 'success' ? (
                                        <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                    {message.text}
                                </div>
                            )}

                            <div className="flex justify-end gap-3">
                                {previewUrl && (
                                    <button
                                        type="button"
                                        onClick={() => { setSelectedFile(null); setPreviewUrl(null); }}
                                        className="px-6 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition"
                                    >
                                        Clear
                                    </button>
                                )}
                                <button
                                    type="submit"
                                    disabled={!selectedFile || loading}
                                    className={`px-8 py-2.5 rounded-xl font-bold transition flex items-center ${!selectedFile || loading
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                        : 'bg-primary-600 text-white hover:bg-primary-700 shadow-md hover:shadow-lg'
                                        }`}
                                >
                                    {loading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Updating...
                                        </>
                                    ) : 'Update Hero Banner'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="mt-8 bg-blue-50 border border-blue-100 p-6 rounded-2xl flex items-start gap-4">
                    <div className="bg-blue-100 p-2 rounded-lg text-blue-600">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <div>
                        <h4 className="font-bold text-blue-900 mb-1">Upload Guide</h4>
                        <p className="text-sm text-blue-800 leading-relaxed md:w-3/4">
                            For best results, use an image with an aspect ratio of 21:9 (e.g., 1920x820 pixels). High resolution photos work best to ensure quality across different screen sizes.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BannerManagement;
